import { CARD_KEYS, BANK_KEYS } from '../constants/keys';

/**
 * Interface for card link mapping
 */
export interface CardLinkMapping {
  sourceUrl: string; // The original bank source URL
  applyUrl: string; // The application URL for the card
  lastUpdated: string; // ISO date when this mapping was last verified/updated
}

/**
 * Interface for bank link mapping
 */
export interface BankLinkMapping {
  sourceUrl: string; // The bank's main website URL
  lastUpdated: string; // ISO date when this mapping was last verified/updated
}

/**
 * Map of card keys to their source URLs
 * This allows us to easily update card information when banks change their offerings
 * The key is the card key from CARD_KEYS
 */
export const cardLinkMap: Record<string, CardLinkMapping> = {
  // Taiwan Shin Bank cards
  [CARD_KEYS.TAISHIN_GOGORO]: {
    sourceUrl: 'https://www.taishinbank.com.tw/TSB/personal/credit/intro/overview/cg042/card001/',
    applyUrl: 'https://www.taishinbank.com.tw/eServiceA/CreditCardAP/apply/index.jsp?pc=273',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.TAISHIN_GOGO]: {
    sourceUrl: 'https://www.taishinbank.com.tw/TSB/personal/credit/intro/overview/cg021/card001/',
    applyUrl: 'https://www.taishinbank.com.tw/eServiceA/CreditCardAP/apply/index.jsp?pc=192',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.TAISHIN_BUSINESS]: {
    sourceUrl: 'https://www.taishinbank.com.tw/TSB/personal/credit/intro/overview/cg005/card001/',
    applyUrl: 'https://www.taishinbank.com.tw/eServiceA/CreditCardAP/apply/index.jsp?pc=207',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.TAISHIN_TZUKUANG]: {
    sourceUrl: 'https://www.taishinbank.com.tw/TSB/personal/credit/intro/overview/cg023/card002/',
    applyUrl: 'https://www.taishinbank.com.tw/eServiceA/CreditCardAP/apply/',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.TAISHIN_JKOS]: {
    sourceUrl: 'https://www.taishinbank.com.tw/TSB/personal/credit/intro/overview/cg038/card001/',
    applyUrl: 'https://www.taishinbank.com.tw/eServiceA/CreditCardAP/apply/index.jsp?pc=260',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.TAISHIN_FLYGO]: {
    sourceUrl: 'https://www.taishinbank.com.tw/TSB/personal/credit/intro/overview/cg018/flygo/',
    applyUrl: 'https://www.taishinbank.com.tw/eServiceA/CreditCardAP/apply/index.jsp?pc=257',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.TAISHIN_SUNROSE]: {
    sourceUrl: 'https://www.taishinbank.com.tw/TSB/personal/credit/intro/overview/cg046/card001/',
    applyUrl:
      'https://www.taishinbank.com.tw/eService/CreditCardAP/apply/m.jsp?c=0B7647D603E04E33C2E2553691E96D3F6CA0CDCF24AD88A77AA2D3DC4E5828BBC841DD&appid=TSCMS',
    lastUpdated: '2025-06-30',
  },
  [CARD_KEYS.TAISHIN_GIVING]: {
    sourceUrl: 'https://www.taishinbank.com.tw/TSB/personal/credit/intro/overview/cg040/card001/',
    applyUrl:
      'https://www.taishinbank.com.tw/eService/CreditCardAP/apply/m.jsp?c=0B7647D603E04E33C2E2553691E96D3F6CA0CDCF24AD88A77AA2D3DC4E5828BBC841DD&appid=TSCMS',
    lastUpdated: '2025-06-30',
  },

  // Cathay Bank cards
  [CARD_KEYS.CATHAY_CUBE]: {
    sourceUrl: 'https://www.cathaybk.com.tw/cathaybk/personal/product/credit-card/cards/cube/',
    applyUrl: 'https://cathaybk.tw/22AAC336K',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.CATHAY_SHOPEE]: {
    sourceUrl: 'https://www.cathaybk.com.tw/cathaybk/personal/product/credit-card/cards/shopee/',
    applyUrl: 'https://www.cathaybk.com.tw/cathaybk/personal/credit-card/cards/choose-card-s1/',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.CATHAY_ASIA_MILES]: {
    sourceUrl:
      'https://www.cathaybk.com.tw/cathaybk/personal/product/credit-card/cards/asia-miles/',
    applyUrl: 'https://www.cathaybk.com.tw/cathaybk/personal/credit-card/cards/choose-card-s1/',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.CATHAY_EVA]: {
    sourceUrl: 'https://www.cathaybk.com.tw/cathaybk/personal/product/credit-card/cards/eva/',
    applyUrl: 'https://www.cathaybk.com.tw/cathaybk/personal/credit-card/cards/choose-card-s1/',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.CATHAY_CASH_BACK]: {
    sourceUrl:
      'https://www.cathaybk.com.tw/cathaybk/personal/product/credit-card/cards/cash-rebate-signature/',
    applyUrl: 'https://www.cathaybk.com.tw/cathaybk/personal/credit-card/cards/choose-card-s1/',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.CATHAY_BUSINESS]: {
    sourceUrl: 'https://www.cathaybk.com.tw/cathaybk/personal/product/credit-card/cards/corporate/',
    applyUrl: 'https://www.cathaybk.com.tw/cathaybk/personal/credit-card/cards/choose-card-s1/',
    lastUpdated: '2025-05-04',
  },
  // CTBC Bank cards
  [CARD_KEYS.CTBC_LINE]: {
    sourceUrl: 'https://www.ctbcbank.com/content/dam/minisite/long/creditcard/LINEPay/index.html',
    applyUrl: 'https://www.ctbcbank.com/content/dam/minisite/long/creditcard/LINEPay/index.html',
    lastUpdated: '2025-05-04',
  },

  // E.SUN Bank cards
  [CARD_KEYS.ESUN_PI]: {
    sourceUrl: 'https://www.esunbank.com/zh-tw/personal/credit-card/intro/co-branded-card/pi-card',
    applyUrl:
      'https://card.esunbank.com.tw/EsunCreditweb/txnproc/selApplyCard?PRJCD=APYCRD0049&param=981747190906876',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.ESUN_UBEAR]: {
    sourceUrl: 'https://www.esunbank.com/zh-tw/personal/credit-card/intro/bank-card/u-bear',
    applyUrl:
      'https://card.esunbank.com.tw/EsunCreditweb/txnproc/selApplyCard?PRJCD=APYCRD0049&param=981747190906889',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.ESUN_KUMAMON]: {
    sourceUrl: 'https://www.esunbank.com/zh-tw/personal/credit-card/intro/bank-card/kumamon_card',
    applyUrl:
      'https://card.esunbank.com.tw/EsunCreditweb/txnproc/selApplyCard?PRJCD=APYCRD0069&param=531747202358769',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.ESUN_UNICARD]: {
    sourceUrl: 'https://www.esunbank.com/zh-tw/personal/credit-card/intro/bank-card/unicard',
    applyUrl:
      'https://card.esunbank.com.tw/EsunCreditweb/txnproc/selApplyCard?PRJCD=APYCRD0070&param=531747202358759',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.ESUN_STARLUX_TITANIUM]: {
    sourceUrl:
      'https://www.esunbank.com/zh-tw/personal/credit-card/intro/co-branded-card/starlux-card',
    applyUrl:
      'https://card.esunbank.com.tw/EsunCreditweb/txnproc/selApplyCard?PRJCD=APYCRD0067&param=641747201840899',
    lastUpdated: '2025-05-16',
  },
  [CARD_KEYS.ESUN_STARLUX_BUSINESS]: {
    sourceUrl:
      'https://www.esunbank.com/zh-tw/personal/credit-card/intro/co-branded-card/starlux-card',
    applyUrl:
      'https://card.esunbank.com.tw/EsunCreditweb/txnproc/selApplyCard?PRJCD=APYCRD0067&param=641747201840899',
    lastUpdated: '2025-05-16',
  },
  [CARD_KEYS.ESUN_STARLUX_WORLD]: {
    sourceUrl:
      'https://www.esunbank.com/zh-tw/personal/credit-card/intro/co-branded-card/starlux-card',
    applyUrl:
      'https://card.esunbank.com.tw/EsunCreditweb/txnproc/selApplyCard?PRJCD=APYCRD0067&param=641747201840899',
    lastUpdated: '2025-05-16',
  },
  [CARD_KEYS.ESUN_STARLUX_WORLD_SUPREME]: {
    sourceUrl:
      'https://www.esunbank.com/zh-tw/personal/credit-card/intro/co-branded-card/starlux-card',
    applyUrl:
      'https://card.esunbank.com.tw/EsunCreditweb/txnproc/selApplyCard?PRJCD=APYCRD0067&param=641747201840899',
    lastUpdated: '2025-05-16',
  },

  // Fubon Bank cards
  [CARD_KEYS.FUBON_J]: {
    sourceUrl: 'https://www.fubon.com/banking/event/hot/omiyagecard/',
    applyUrl: 'https://www.fubon.com/banking/personal/credit_card/apply/index.html',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.FUBON_DIGITAL]: {
    sourceUrl:
      'https://www.fubon.com/banking/personal/credit_card/all_card/digitallife/digitallife.htm',
    applyUrl: 'https://www.fubon.com/banking/personal/credit_card/apply/index.html',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.FUBON_MOMO]: {
    sourceUrl: 'https://www.fubon.com/banking/personal/credit_card/all_card/momo/momo.htm',
    applyUrl: 'https://www.fubon.com/banking/personal/credit_card/all_card/momo/momo.htm',
    lastUpdated: '2025-05-10',
  },
  [CARD_KEYS.FUBON_LIFESTYLE]: {
    sourceUrl:
      'https://www.fubon.com/banking/personal/credit_card/all_card/lifestyle/lifestyle.htm',
    applyUrl: 'https://www.fubon.com/banking/personal/credit_card/all_card/lifestyle/lifestyle.htm',
    lastUpdated: '2025-05-10',
  },
  [CARD_KEYS.FUBON_COSTCO]: {
    sourceUrl: 'https://www.fubon.com/banking/personal/credit_card/all_card/costco/costco.htm',
    applyUrl: 'https://www.fubon.com/banking/personal/credit_card/all_card/costco/costco.htm',
    lastUpdated: '2025-05-10',
  },
  [CARD_KEYS.FUBON_INSURANCE]: {
    sourceUrl:
      'https://www.fubon.com/banking/personal/credit_card/all_card/insurance/insurance.htm',
    applyUrl: 'https://www.fubon.com/banking/personal/credit_card/all_card/insurance/insurance.htm',
    lastUpdated: '2025-05-10',
  },

  // Sino Bank cards
  [CARD_KEYS.SINO_DAWHO]: {
    sourceUrl:
      'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/bankcard/DAWHO.html',
    applyUrl: 'https://bank.sinopac.com/sinopacBT/webevents/1809_mgm/index.html?c=PBYN759',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.SINO_WBFU]: {
    sourceUrl:
      'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/bankcard/WBFU.html',
    applyUrl: 'https://bank.sinopac.com/sinopacBT/webevents/1809_mgm/index.html?c=PBYN759',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.SINO_SPORT]: {
    sourceUrl:
      'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/bankcard/sportcard.html',
    applyUrl: 'https://bank.sinopac.com/sinopacBT/webevents/1809_mgm/index.html?c=PBYN759',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.SINO_GREEN]: {
    sourceUrl:
      'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/bankcard/cashcard.html',
    applyUrl: 'https://bank.sinopac.com/sinopacBT/webevents/1809_mgm/index.html?c=PBYN759',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.SINO_DUE_CURRENCY]: {
    sourceUrl:
      'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/bankcard/dual-currency-card.html',
    applyUrl: 'https://bank.sinopac.com/sinopacBT/webevents/1809_mgm/index.html?c=PBYN759',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.SINO_CASHCARD_JCB]: {
    sourceUrl:
      'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/bankcard/cashcardJCB.html',
    applyUrl: 'https://bank.sinopac.com/sinopacBT/webevents/1809_mgm/index.html?c=PBYN759',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.SINO_MITSUI]: {
    sourceUrl:
      'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/co-brand/mitsui-outlet-park-card.html',
    applyUrl: 'https://bank.sinopac.com/sinopacBT/webevents/1809_mgm/index.html?c=PBYN759',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.SINO_TAXI]: {
    sourceUrl:
      'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/co-brand/taxicard.html',
    applyUrl: 'https://bank.sinopac.com/sinopacBT/webevents/1809_mgm/index.html?c=PBYN759',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.SINO_DAWAY]: {
    sourceUrl:
      'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/bankcard/DAWAY.html',
    applyUrl: 'https://bank.sinopac.com/sinopacBT/webevents/1809_mgm/index.html?c=PBYN759',
    lastUpdated: '2025-05-04',
  },

  // DBS Bank cards
  [CARD_KEYS.DBS_ECO]: {
    sourceUrl: 'https://www.dbs.com.tw/personal-zh/cards/dbs_eco/index.html',
    applyUrl:
      'https://internet-banking.dbs.com.tw/credit-card-application/?cardlogo=310&sc=0c40b28ad098bab25f32d395acc2ca47',
    lastUpdated: '2025-08-31',
  },
  [CARD_KEYS.DBS_TRAVEL_WORLD_ELITE]: {
    sourceUrl: 'https://www.dbs.com.tw/personal-zh/cards/travel_world_elite/index.html',
    applyUrl:
      'https://internet-banking.dbs.com.tw/credit-card-application/?cardlogo=310&sc=0c40b28ad098bab25f32d395acc2ca47',
    lastUpdated: '2025-08-31',
  },
  [CARD_KEYS.DBS_FUNLIFE]: {
    sourceUrl: 'https://www.dbs.com.tw/personal-zh/cards/funlife/index.html',
    applyUrl:
      'https://internet-banking.dbs.com.tw/credit-card-application/?cardlogo=310&sc=0c40b28ad098bab25f32d395acc2ca47',
    lastUpdated: '2025-08-31',
  },
  [CARD_KEYS.DBS_TRAVEL_WORLD]: {
    sourceUrl: 'https://www.dbs.com.tw/personal-zh/cards/travelworld/index.html',
    applyUrl:
      'https://internet-banking.dbs.com.tw/credit-card-application/?cardlogo=310&sc=0c40b28ad098bab25f32d395acc2ca47',
    lastUpdated: '2025-08-31',
  },

  // UBOT cards
  [CARD_KEYS.UBOT_MCARD]: {
    sourceUrl: 'https://activity.ubot.com.tw/2025Mcard/index.htm',
    applyUrl: 'https://activity.ubot.com.tw/2025Mcard/index.htm',
    lastUpdated: '2025-05-04',
  },
  [CARD_KEYS.UBOT_JIHO]: {
    sourceUrl: 'https://card.ubot.com.tw/CardDetail/cardDetail202',
    applyUrl:
      'https://card.ubot.com.tw/eCard/dspCardOnLineApply.aspx?GID=59&IDE=A&strID=2008060014&PJN=JHA&UTC=&USC=2981778548',
    lastUpdated: '2025-05-04',
  },

  // American Express cards
  [CARD_KEYS.AMERICANEXPRESS_PLAT]: {
    sourceUrl:
      'https://www.americanexpress.com/zh-tw/charge-cards/plat/?linknav=tw-amex-cardshop-allcards-learn-Plat',
    applyUrl:
      'https://www.americanexpress.com/zh-tw/charge-cards/plat/?linknav=tw-amex-cardshop-allcards-learn-Plat',
    lastUpdated: '2025-05-04',
  },

  // HSBC cards
  [CARD_KEYS.HSBC_CASHBACK]: {
    sourceUrl: 'https://www.hsbc.com.tw/credit-cards/products/cashback-signature/',
    applyUrl: 'https://www.hsbc.com.tw/credit-cards/products/cashback-signature/',
    lastUpdated: '2025-05-12',
  },
  [CARD_KEYS.HSBC_TRAVEL_INFINITE]: {
    sourceUrl: 'https://www.hsbc.com.tw/credit-cards/products/travel/visa-infinite/',
    applyUrl: 'https://www.hsbc.com.tw/credit-cards/products/travel/visa-infinite/',
    lastUpdated: '2025-05-12',
  },
  [CARD_KEYS.HSBC_TRAVEL_ONE]: {
    sourceUrl: 'https://www.hsbc.com.tw/credit-cards/products/travelone/',
    applyUrl: 'https://www.hsbc.com.tw/credit-cards/products/travelone/',
    lastUpdated: '2025-05-12',
  },
  [CARD_KEYS.HSBC_TRAVEL_ONE_SIGNATURE]: {
    sourceUrl: 'https://www.hsbc.com.tw/credit-cards/products/travelone-signature/',
    applyUrl: 'https://www.hsbc.com.tw/credit-cards/products/travelone-signature/',
    lastUpdated: '2025-05-12',
  },
};

/**
 * Map of bank keys to their source URLs
 * This allows us to easily update bank information when they change their websites
 * The key is the bank key from BANK_KEYS
 */
export const bankLinkMap: Record<string, BankLinkMapping> = {
  [BANK_KEYS.TAISHIN]: {
    sourceUrl: 'https://www.taishinbank.com.tw/TSB/personal/credit/intro/overview/',
    lastUpdated: '2025-05-04',
  },
  [BANK_KEYS.CATHAY]: {
    sourceUrl: 'https://www.cathaybk.com.tw/cathaybk/personal/product/credit-card/cards/',
    lastUpdated: '2025-05-04',
  },
  [BANK_KEYS.CTBC]: {
    sourceUrl:
      'https://www.ctbcbank.com/twrbo/zh_tw/cc_index/cc_product/cc_introduction_index.html',
    lastUpdated: '2025-05-04',
  },
  [BANK_KEYS.ESUN]: {
    sourceUrl: 'https://www.esunbank.com/zh-tw/personal/credit-card/intro',
    lastUpdated: '2025-05-04',
  },
  [BANK_KEYS.FUBON]: {
    sourceUrl: 'https://www.fubon.com/banking/personal/credit_card/all_card/all_card.htm',
    lastUpdated: '2025-05-04',
  },
  [BANK_KEYS.SINO]: {
    sourceUrl: 'https://bank.sinopac.com/sinopacBT/personal/credit-card/introduction/list.html',
    lastUpdated: '2025-05-04',
  },
  [BANK_KEYS.DBS]: {
    sourceUrl:
      'https://www.dbs.com.tw/personal-zh/cards/default.page?pid=tw-pweb-personal_zh-cards-header',
    lastUpdated: '2025-05-04',
  },
  [BANK_KEYS.UBOT]: {
    sourceUrl: 'https://card.ubot.com.tw/Card?category=%E7%86%B1%E9%96%80%E6%8E%A8%E8%96%A6',
    lastUpdated: '2025-05-04',
  },
  [BANK_KEYS.AMERICANEXPRESS]: {
    sourceUrl:
      'https://www.americanexpress.com/zh-tw/credit-cards/all-cards/?inav=ta_menu_card_personal',
    lastUpdated: '2025-05-04',
  },
};

/**
 * Find a card key by its source URL
 * @param sourceUrl The bank's source URL for the card
 * @returns The corresponding card key or undefined if not found
 */
export function findCardKeyBySourceUrl(sourceUrl: string): string | undefined {
  // Normalize the URL (remove protocol, www, trailing slashes)
  const normalizedUrl = sourceUrl
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '');

  // Look for an exact match first
  for (const [cardKey, mapping] of Object.entries(cardLinkMap)) {
    const mappingUrl = mapping.sourceUrl
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/$/, '');

    if (mappingUrl === normalizedUrl) {
      return cardKey;
    }
  }

  // If no exact match, try to find a partial match
  for (const [cardKey, mapping] of Object.entries(cardLinkMap)) {
    const mappingUrl = mapping.sourceUrl
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/$/, '');

    if (normalizedUrl.includes(mappingUrl) || mappingUrl.includes(normalizedUrl)) {
      return cardKey;
    }
  }

  return undefined;
}

/**
 * Find all card keys that need to be updated based on a cutoff date
 * @param cutoffDate ISO date string (YYYY-MM-DD)
 * @returns Array of card keys that need updating
 */
export function findCardsNeedingUpdate(cutoffDate: string): string[] {
  const cutoffTimestamp = new Date(cutoffDate).getTime();

  return Object.entries(cardLinkMap)
    .filter(([_, mapping]) => {
      const lastUpdatedTimestamp = new Date(mapping.lastUpdated).getTime();
      return lastUpdatedTimestamp < cutoffTimestamp;
    })
    .map(([cardKey, _]) => cardKey);
}

/**
 * Find all bank keys that need to be updated based on a cutoff date
 * @param cutoffDate ISO date string (YYYY-MM-DD)
 * @returns Array of bank keys that need updating
 */
export function findBanksNeedingUpdate(cutoffDate: string): string[] {
  const cutoffTimestamp = new Date(cutoffDate).getTime();

  return Object.entries(bankLinkMap)
    .filter(([_, mapping]) => {
      const lastUpdatedTimestamp = new Date(mapping.lastUpdated).getTime();
      return lastUpdatedTimestamp < cutoffTimestamp;
    })
    .map(([bankKey, _]) => bankKey);
}

/**
 * Update the lastUpdated date for a card mapping
 * @param cardKey The card key to update
 * @param newDate Optional new date (defaults to today)
 */
export function updateCardMappingDate(cardKey: string, newDate?: string): void {
  if (!cardLinkMap[cardKey]) {
    console.error(`Card key ${cardKey} not found in mapping`);
    return;
  }

  const today = newDate || new Date().toISOString().split('T')[0];
  cardLinkMap[cardKey].lastUpdated = today;
}

/**
 * Update the lastUpdated date for a bank mapping
 * @param bankKey The bank key to update
 * @param newDate Optional new date (defaults to today)
 */
export function updateBankMappingDate(bankKey: string, newDate?: string): void {
  if (!bankLinkMap[bankKey]) {
    console.error(`Bank key ${bankKey} not found in mapping`);
    return;
  }

  const today = newDate || new Date().toISOString().split('T')[0];
  bankLinkMap[bankKey].lastUpdated = today;
}
